/**
 * Adapters from upsell data sources (voucher pools, GQL course, recommendations)
 * to UdemyShoppingItem so cart row components can render them.
 */
import type {CourseForCartUpsell} from '@udemy/react-certification-preparation';
import type {VoucherPoolPrice} from '@udemy/react-certification-vouchers';
import {CertificationVoucherBuyable, ShoppingItem as UdemyShoppingItem} from '@udemy/shopping';

import type {CoursePrice} from 'src/hooks/use-course-price';

export type {CourseForCartUpsell};

/** Instruction level of the course */
export enum DifficultyLevel {
    /** Item is appropriate for all skill levels */
    AllLevels = 'ALL_LEVELS',
    /** Item is for beginners */
    Beginner = 'BEGINNER',
    /** Item is for students with expert knowledge */
    Expert = 'EXPERT',
    /** Item is for students with intermediates knowledge */
    Intermediate = 'INTERMEDIATE',
}

export interface VoucherPoolUpsellInput {
    /** Pool ID — used as the cart item id. */
    id: number | string;
    /** Exam ID — used as offer_id; uniquely identifies the exam across pools. */
    examId: string;
    title: string;
    imageUrl: string;
    slug: string;
    /** From voucherPools[].expirationThresholdInMonths in the GQL response. */
    expirationThresholdInMonths: number;
    /** From exam.providerDetails.name (useExamByTopicId.issuerName). */
    issuerName?: string;
}

export function buildVoucherShoppingItemFromPool(
    pool: VoucherPoolUpsellInput,
    price?: VoucherPoolPrice,
): UdemyShoppingItem | null {
    // TODO: Add some kind of error state to display for buyables with no prices.
    // For now, do not show the whole item if one is unavailable.
    if (!price) {
        return null;
    }
    const poolId = typeof pool.id === 'string' ? parseInt(pool.id, 10) : pool.id;
    const buyable = {
        id: poolId,
        title: pool.title,
        buyable_object_type: 'certification_voucher',
        // TODO: We need to include 'certification_voucher' here once we have
        // a new class for certification vouchers in the event schema.
        _class: 'course',
        frontendTrackingId: String(poolId),
        image_100x100: pool.imageUrl,
        url: `/certification-vouchers/${pool.slug}/`,
        offer_id: pool.examId,
        issuerName: pool.issuerName,
        expiration_date_in_months: pool.expirationThresholdInMonths,
    } as CertificationVoucherBuyable & {issuerName: string};
    const item = {
        buyable,
        list_price: price.list_price,
        purchase_price: price.price,
        price_serve_tracking_id: price.price_serve_tracking_id,
    } as UdemyShoppingItem;
    return item;
}

export function buildShoppingItemFromGqlCourse(
    gql: CourseForCartUpsell | null,
    price?: CoursePrice | null,
    gettext?: (s: string) => string,
): UdemyShoppingItem | null {
    if (gql == null) {
        throw new Error('buildShoppingItemFromGqlCourse: course is required');
    }
    // TODO: Add some kind of error state to display for buyables with no prices.
    // For now, do not show the whole item if one is unavailable, as
    if (!price) {
        return null;
    }
    const id = parseInt(gql.id, 10);
    const numId = Number.isNaN(id) ? 0 : id;

    const item = {
        buyable: {
            id: numId,
            title: gql.title ?? '',
            buyable_object_type: 'course',
            _class: 'course',
            frontendTrackingId: gql.id,
            image_240x135: gql.images?.px240x135 ?? '',
            image_480x270: gql.images?.px480x270 ?? '',
            url: gql.urlCourseLanding,
            visible_instructors: (gql.instructors ?? []).map((i) => ({display_name: i.name ?? ''})),
            rating: gql.rating?.average ?? undefined,
            num_reviews: gql.rating?.count ?? undefined,
            estimated_content_length: gql.durationInSeconds ?? undefined,
            num_published_lectures: gql.curriculum?.contentCounts?.lecturesCount ?? undefined,
            instructional_level_simple: gettext
                ? difficultyLevelLocalized(gql.level, gettext)
                : undefined,
        } as UdemyShoppingItem['buyable'],
        list_price: price.list_price,
        purchase_price: price.purchase_price,
        saving_price: price.saving_price,
        current_discount: price?.current_discount,
    } as UdemyShoppingItem;
    return item;
}

/**
 * Returns true when a shopping item has a zero list price and pricing has finished loading.
 * Used to hide free courses from upsell rows — but only once we know the real price.
 */
export function isEffectivelyFree(item: UdemyShoppingItem, isPriceLoading: boolean): boolean {
    return !isPriceLoading && item.list_price.amount === 0 && !item.list_price.price_string;
}

function difficultyLevelLocalized(
    difficultyLevel: DifficultyLevel | null | undefined,
    gettext: (s: string) => string,
): string {
    switch (difficultyLevel) {
        case DifficultyLevel.Beginner:
            return gettext('Beginner');
        case DifficultyLevel.Intermediate:
            return gettext('Intermediate');
        case DifficultyLevel.Expert:
            return gettext('Expert');
        default:
            return gettext('All Levels');
    }
}
